import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Body1Strong,
  Caption1Stronger,
  Menu,
  MenuDivider,
  MenuGroupHeader,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MenuGroup,
} from '@fluentui/react-components';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { FormattedMessage, useIntl } from 'react-intl';

import { reportMixPanel } from '@55degrees/consent-tracker';
import { useLocation } from 'react-router';
import {
  CustomMenuButtonDataSet,
  MenuContainer,
  MenuLabelWrapper,
  MenuHeaderWrapper,
  MenuItemFull,
  CustomBadge,
} from './styling';
import { ProductFruitsReporter } from '../../Helpers/ProductFruitsInit';
import { MAX_NAME_LENGTH_BEFORE_TRUNCATE } from '../../../config';
import useSavedDatasets from '../../../hooks/useSavedDatasets';
import DatasetSection from './DatasetSection';
import CurrentlyViewingSection from './CurrentlyViewingSection';
import { A3DocumentContext } from '../../../contexts/A3DocumentContext';
import DataLoaderContext from '../../../contexts/DataLoaderContext';

function LoadDataDropdown({
  openFileLoader,
  openDeleteDatasetDialog,
  isDataSetDropdownOpen,
  setIsDataSetDropdownOpen,
  isNormalScreenSize,
  openDataLoader,
  loadDatasetFromDropdown,
}) {
  const { a3DocumentTitle, loadA3ExampleData } = useContext(A3DocumentContext);
  const { setTrigger } = useContext(DataLoaderContext);
  const location = useLocation();
  const intl = useIntl();

  const isIndexPage = location.pathname === '/';
  const dataSetName = a3DocumentTitle || 'Example data';
  const isNameTruncated = dataSetName.length > MAX_NAME_LENGTH_BEFORE_TRUNCATE;
  const menuButtonName = isNameTruncated
    ? `${dataSetName.substring(0, MAX_NAME_LENGTH_BEFORE_TRUNCATE)}..`
    : dataSetName;

  const {
    favoriteDatasets,
    filteredStrippedDatasets,
    favoriteDatasetById,
    isLoading: isLoadingDatasets,
  } = useSavedDatasets();

  const toggleDropdown = () => setIsDataSetDropdownOpen((prev) => !prev);

  const onLoadFileLoaderClick = () => {
    openFileLoader();
    ProductFruitsReporter.track('openExternalDL');
  };

  const onLoadExampleDataClick = () => {
    loadA3ExampleData();
  };

  const onBetaLoaderClickCreate = () => {
    setTrigger('Create');
    openDataLoader('create');
    ProductFruitsReporter.track('openCreateDS');
    reportMixPanel('Data Create Start');
  };

  const onBetaLoaderClickManage = () => {
    setTrigger('Find');
    openDataLoader('manage');
    reportMixPanel('Data Manage Start');
  };

  const onFavoriteDatasetClick = (uuid) => {
    const selectedDataset = [...favoriteDatasets, ...filteredStrippedDatasets].find(
      (dataset) => dataset.uuid === uuid
    );

    if (selectedDataset) {
      let totalNumber = [...favoriteDatasets, ...filteredStrippedDatasets].reduce(
        (acc, dataset) => acc + (dataset.favorite ? 1 : 0),
        0
      );
      totalNumber += selectedDataset.favorite ? -1 : 1;

      reportMixPanel('Data Favorite Change', {
        Trigger: selectedDataset.favorite ? 'Remove' : 'Add',
        'Total number': totalNumber,
      });
    }

    favoriteDatasetById(uuid);
  };

  const isCurrentlyViewingShown =
    filteredStrippedDatasets.find((dataset) => dataset.name === dataSetName) &&
    dataSetName !== 'Example data';

  const innerLabelName = !isIndexPage
    ? menuButtonName
    : intl.formatMessage({
        id: 'landing.page.data.load.dropdown.inner.label',
        defaultMessage: 'Select a data set',
      });

  const onDatasetLoadClick = (uuid) => {
    setTrigger('Dropdown');
    loadDatasetFromDropdown(uuid);
  };

  return (
    <MenuContainer>
      <MenuLabelWrapper>
        {isNormalScreenSize ? (
          <Caption1Stronger>
            <FormattedMessage
              id="loadData.aside.label"
              defaultMessage="DATA SET"
              description="Text Aside of Menu"
            />
          </Caption1Stronger>
        ) : (
          <NoteAddIcon fontSize="small" />
        )}
      </MenuLabelWrapper>
      <Menu
        open={isDataSetDropdownOpen}
        onOpenChange={(e, data) => {
          setIsDataSetDropdownOpen(data.open);
        }}
      >
        <MenuTrigger disableButtonEnhancement>
          <CustomMenuButtonDataSet id="a3-ds-dropdown">
            <Body1Strong onClick={toggleDropdown}>{innerLabelName}</Body1Strong>
          </CustomMenuButtonDataSet>
        </MenuTrigger>
        <MenuPopover style={{ maxWidth: '100%', minWidth: '380px' }}>
          <MenuList>
            {isCurrentlyViewingShown && (
              <MenuGroup style={{ padding: '0.5rem 0' }}>
                {!isIndexPage && (
                  <CurrentlyViewingSection
                    currentDataSetName={dataSetName}
                    onDatasetLoadClick={onDatasetLoadClick}
                    openDeleteDatasetDialog={openDeleteDatasetDialog}
                    onFavoriteDatasetClick={onFavoriteDatasetClick}
                    openDataLoader={openDataLoader}
                  />
                )}
              </MenuGroup>
            )}
            <MenuGroup style={{ padding: '0.5rem 0' }}>
              <MenuGroupHeader>
                <MenuHeaderWrapper>
                  <FormattedMessage
                    id="loadData.menu.title.create-manage-dataset"
                    defaultMessage="Create & Save Data Sets"
                    description="Menu Group Text"
                  />
                </MenuHeaderWrapper>
                <CustomBadge shape="circular" size="medium">
                  <FormattedMessage
                    id="loadData.menu.badge.new"
                    defaultMessage="New"
                    description="Menu Group Text"
                  />
                </CustomBadge>
              </MenuGroupHeader>
              <MenuItemFull onClick={onBetaLoaderClickCreate} id="a3-ds-create">
                <FormattedMessage
                  id="loadData.menu.item.create-dataset"
                  defaultMessage="Create New Data Set"
                  description="Menu Group Text"
                />
              </MenuItemFull>
              <MenuItemFull onClick={onBetaLoaderClickManage} id="a3-ds-manage">
                <FormattedMessage
                  id="loadData.menu.item.manage-dataset"
                  defaultMessage="Find or Manage Data Sets"
                  description="Menu Group Text"
                />
              </MenuItemFull>
            </MenuGroup>
            <DatasetSection
              favoriteDatasets={favoriteDatasets}
              strippedDatasets={filteredStrippedDatasets}
              isLoading={isLoadingDatasets}
              onDatasetLoadClick={onDatasetLoadClick}
              onFavoriteClick={onFavoriteDatasetClick}
              openDataLoader={openDataLoader}
            />
            <MenuDivider />
            <MenuGroup style={{ padding: '0.5rem 0' }}>
              <MenuGroupHeader>
                <MenuHeaderWrapper>
                  <FormattedMessage
                    id="loadData.menu.title.external-data"
                    defaultMessage="Beyond This Project"
                    description="Menu Group Text"
                  />
                </MenuHeaderWrapper>
              </MenuGroupHeader>
              <MenuItemFull onClick={onLoadFileLoaderClick} id="a3-ds-external">
                <FormattedMessage
                  id="loadData.menu.item.loadExternal"
                  defaultMessage="Load an External Data File"
                  description="Menu Group Text"
                />
              </MenuItemFull>
              <MenuItemFull onClick={onLoadExampleDataClick} id="a3-ds-example">
                <FormattedMessage
                  id="loadData.menu.item.loadExample"
                  defaultMessage="Explore Using Example Data"
                  description="Menu Group Text"
                />
              </MenuItemFull>
            </MenuGroup>
          </MenuList>
        </MenuPopover>
      </Menu>
    </MenuContainer>
  );
}

LoadDataDropdown.propTypes = {
  openFileLoader: PropTypes.func.isRequired,
  isNormalScreenSize: PropTypes.bool.isRequired,
  openDataLoader: PropTypes.func.isRequired,
  loadDatasetFromDropdown: PropTypes.func.isRequired,
};

export default LoadDataDropdown;
